import { useEffect, useRef, useState } from "react";
import { Film, Scissors, Repeat, Trash2, Loader2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import VideoTrimModal from "@/components/VideoTrimModal";
import api from "@/lib/api";
import { videoPoster, uploadStepClip, STEP_CLIP_MAX_MB } from "@/lib/stepClip";

// Editor-side clip tools for one step: re-trim, swap, sound, remove. onChanged() reloads the manual.
export default function StepClipEditor({ manualId, index, clip, onClose, onChanged }) {
  const open = index != null && !!clip;
  const fileRef = useRef(null);
  const [srcFile, setSrcFile] = useState(null);
  const [trimFile, setTrimFile] = useState(null); // { file, duration, start, mode: "retrim" | "swap" }
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) { setSrcFile(null); return; }
    let alive = true;
    api.get(`/manuals/${manualId}/steps/${index}/clip/file`, { responseType: "blob" })
      .then(({ data }) => alive && setSrcFile(new File([data], "clip.mp4", { type: "video/mp4" })))
      .catch(() => {});
    return () => { alive = false; };
  }, [open, manualId, index]);

  const srcDur = clip?.source_duration || clip?.duration || 10;
  const canRetrim = srcDur > 10.5;

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); await onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — try again"); }
    finally { setBusy(""); }
  };

  const retrim = (start) => run("Updating slice…", async () => {
    await api.patch(`/manuals/${manualId}/steps/${index}/clip/trim`, { trim_start: start });
    toast.success("Clip re-trimmed — re-render your video to see it");
  });

  const swapUpload = (file, start) => run("Uploading new clip…", async () => {
    await uploadStepClip(manualId, index, file, clip?.keep_sound, (f) => setBusy(`Uploading new clip · ${Math.round(f * 100)}%`), true, start);
    toast.success("Clip swapped — re-render your video to see it");
  });

  const onPickSwap = async (file) => {
    if (!file) return;
    if (file.size > STEP_CLIP_MAX_MB * 1048576) { toast.error(`This clip is ${(file.size / 1048576).toFixed(1)}MB — the limit is ${STEP_CLIP_MAX_MB}MB`); return; }
    let duration = 0;
    try { ({ duration } = await videoPoster(file)); } catch { duration = 0; }
    if (duration > 20.5) { toast.error(`This clip is ${Math.round(duration)}s — clips can be up to 20 seconds`); return; }
    if (duration > 10.5) setTrimFile({ file, duration, start: 0, mode: "swap" });
    else swapUpload(file, 0);
  };

  const toggleSound = () => run("Saving…", async () => {
    await api.patch(`/manuals/${manualId}/steps/${index}/clip`, { keep_sound: !clip.keep_sound });
  });

  const remove = () => run("Removing…", async () => {
    await api.delete(`/manuals/${manualId}/steps/${index}/clip`);
    toast.success("Clip removed — this step now uses its photo");
    onClose();
  });

  const Btn = ({ icon: Icon, label, onClick, testid, disabled, danger }) => (
    <button type="button" onClick={onClick} disabled={!!busy || disabled} data-testid={testid}
      className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-extrabold border-2 transition-colors disabled:opacity-40 ${danger ? "border-[#dc2626] text-[#f87171] hover:bg-[#dc2626]/15" : "border-[#5d8a52] text-white hover:bg-white/10"}`}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <>
      <Dialog open={open && !trimFile} onOpenChange={(o) => !o && !busy && onClose()}>
        <DialogContent className="bg-[#0f0f16] border-2 border-[#5d8a52] text-white max-w-md" data-testid="step-clip-editor">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#f0c975] text-lg font-extrabold"><Film size={18} /> Step {index != null ? index + 1 : ""} video clip</DialogTitle>
            <DialogDescription className="text-white/70">
              Plays {Math.round(clip?.duration || 10)}s{canRetrim ? ` from 0:${String(Math.floor(clip?.trim_start || 0)).padStart(2, "0")}` : ""} · {clip?.has_audio ? (clip?.keep_sound ? "clip sound on" : "clip sound off") : "no clip sound"}
            </DialogDescription>
          </DialogHeader>
          {busy && <p className="flex items-center gap-2 text-sm font-bold text-[#f0c975]" data-testid="step-clip-busy"><Loader2 size={15} className="spin" /> {busy}</p>}
          <div className="space-y-2">
            <Btn icon={Scissors} label={canRetrim ? "Re-trim" : "Re-trim (clip is 10s or shorter)"} testid="step-clip-retrim" disabled={!canRetrim || !srcFile}
              onClick={() => setTrimFile({ file: srcFile, duration: srcDur, start: clip?.trim_start || 0, mode: "retrim" })} />
            <Btn icon={Repeat} label="Swap clip" testid="step-clip-swap" onClick={() => fileRef.current?.click()} />
            {clip?.has_audio && <Btn icon={clip?.keep_sound ? VolumeX : Volume2} label={clip?.keep_sound ? "Turn clip sound off" : "Turn clip sound on"} testid="step-clip-sound" onClick={toggleSound} />}
            <Btn icon={Trash2} label="Remove clip (use the photo)" testid="step-clip-remove" danger onClick={remove} />
          </div>
          <input ref={fileRef} type="file" accept="video/*" className="hidden" data-testid="step-clip-swap-file"
            onChange={(e) => { onPickSwap(e.target.files?.[0]); e.target.value = ""; }} />
        </DialogContent>
      </Dialog>
      {trimFile && (
        <VideoTrimModal file={trimFile.file} duration={trimFile.duration} initialStart={trimFile.start}
          onClose={() => setTrimFile(null)}
          onConfirm={(start) => { const t = trimFile; setTrimFile(null); if (t.mode === "retrim") retrim(start); else swapUpload(t.file, start); }} />
      )}
    </>
  );
}
